import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Clock, ArrowRight } from "lucide-react";
import coursesBackground from "@/assets/courses-background.jpg";

interface Course {
  id: string;
  title: string;
  description: string;
  duration: string;
  status: "available" | "coming-soon" | "new" | "featured";
  image: string;
  category: string;
}

const courses: Course[] = [
  {
    id: "data-lifecycle-storytelling-workshop",
    title: "Data Life Cycle & Storytelling Workshop",
    description: "Learn the full data journey from real experts: Data Engineers, Data Scientists, and Data Analysts. Hands-on knowledge transfer from field professionals.",
    duration: "2 days intensive",
    status: "new",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
    category: "Workshop"
  },
  {
    id: "fasttrack-development-program",
    title: "FTS Development Summer Program",
    description: "Rotate through all major project departments and stages — from initiation to closing — guided by real industry experts.",
    duration: "8 weeks",
    status: "featured",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop",
    category: "Program"
  },
  {
    id: "power-automate-workflows",
    title: "Power Automate Workflows for Project Management",
    description: "Learn how to create automated workflows using Microsoft Power Automate to streamline project management processes.",
    duration: "3 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
    category: "Power Platform"
  },
  {
    id: "data-journey-power-bi",
    title: "Data Journey by Power BI",
    description: "Master data analysis and visualization using Microsoft Power BI to transform raw data into actionable insights.",
    duration: "5 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
    category: "Power Platform"
  },
  {
    id: "data-transformation-advanced",
    title: "Data Transformation with Advanced Queries",
    description: "Master data transformation techniques using advanced queries to clean, reshape, and analyze project data.",
    duration: "4 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&h=400&fit=crop",
    category: "Data"
  },
  {
    id: "project-digital-transformation",
    title: "Power Platform End to End Solution",
    description: "Learn how to leverage Microsoft Power Platform and advanced 360 virtual reporting to transform project planning.",
    duration: "6 weeks",
    status: "featured",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=400&fit=crop",
    category: "Power Platform"
  },
  {
    id: "ai-agent-development-n8n",
    title: "AI Agent Development with N8N",
    description: "Build intelligent automation workflows and AI agents using N8N's powerful visual workflow builder.",
    duration: "3 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop",
    category: "AI & Automation"
  },
  {
    id: "ai-digital-transformation-integrated",
    title: "AI Agent + Digital Transformation Integrated Program",
    description: "Comprehensive program combining AI Agent Development with N8N and Digital Transformation using Power Platform.",
    duration: "9 weeks",
    status: "available",
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=400&fit=crop",
    category: "AI & Automation"
  }
];

const categories = ["All", "Workshop", "Program", "Power Platform", "Data", "AI & Automation"];

const getStatusBadge = (status: Course["status"]) => {
  switch (status) {
    case "new":
      return <Badge className="bg-green-500 text-white">NEW</Badge>;
    case "featured":
      return <Badge className="bg-primary text-primary-foreground">FEATURED</Badge>;
    case "coming-soon":
      return <Badge variant="secondary">COMING SOON</Badge>;
    default:
      return <Badge variant="outline">AVAILABLE</Badge>;
  }
};

const Courses = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center">
          {/* Full-size Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${coursesBackground})` }}
          >
            <div className="absolute inset-0 bg-black/50" />
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center w-full">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Master Digital Skills. <span className="text-white/80">FTS Academy.</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Transform your career with expert-led training in Microsoft Power Platform, AI, and digital innovation.
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto">
              <div className="relative flex items-center">
                <Input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-4 pr-12 text-base border-0 rounded-md bg-white/95 w-full shadow-lg"
                />
                <Search className="absolute right-4 h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? "bg-foreground text-background"
                      : "bg-muted text-foreground hover:bg-muted/80"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Course Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/course/${course.id}`}
                  className="group bg-card rounded-lg overflow-hidden border border-border hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={course.image}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3">
                      {getStatusBadge(course.status)}
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-muted-foreground mb-2">{course.category}</p>
                    <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {course.duration}
                      </div>
                      <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredCourses.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No courses found matching your criteria.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Courses;
